import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { subscribeWithSelector } from 'zustand/middleware';
import type { RegistrationFormValues } from '@/features/cleaner/apply-cleaner/schemas';
import { STEPS } from '@/features/cleaner/apply-cleaner/constant';
import { registrationSchema } from '@/features/cleaner/apply-cleaner/schemas';
import { isPersonalInfoStepDisabledWithOverride } from '@/featureFlag/disablePersonalInfoStep';

interface CleanerRegistrationState {
  // Current step
  currentStep: number;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  jumpToStep: (step: number) => void;

  // Form data
  formData: Partial<RegistrationFormValues>;
  updateFormData: (data: Partial<RegistrationFormValues>) => void;
  setFormData: (data: Partial<RegistrationFormValues>) => void;
  updateFormField: (
    field: keyof RegistrationFormValues,
    value: unknown
  ) => void;
  resetFormData: () => void;

  // Validation
  errors: Partial<Record<keyof RegistrationFormValues, string[]>>;
  setErrors: (
    errors: Partial<Record<keyof RegistrationFormValues, string[]>>
  ) => void;
  clearErrors: () => void;
  validateCurrentStep: () => {
    isValid: boolean;
    errors: Partial<Record<keyof RegistrationFormValues, string[]>>;
  };
  isStepValid: (step: number) => boolean;

  // Step completion status
  completedSteps: Set<number>;
  markStepCompleted: (step: number) => void;
  unmarkStepCompleted: (step: number) => void;
  isStepCompleted: (step: number) => boolean;
  getStepProgress: () => {
    completed: number;
    total: number;
    percentage: number;
  };

  // Registration status
  isSignedUp: boolean;
  setIsSignedUp: (signedUp: boolean) => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  loadingStep: number | null;
  setLoadingStep: (step: number | null) => void;

  // Navigation helpers
  canProceedToStep: (step: number) => boolean;
  getFirstIncompleteStep: () => number;
  getNextAccessibleStep: () => number;
  getPreviousAccessibleStep: () => number;
  getAllAccessibleSteps: () => number[];

  // Auto-save
  lastSaved: string | null;
  setLastSaved: (timestamp: string) => void;
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;

  // Error handling
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Reset utility
  reset: () => void;
  clearAllData: () => void;

  // Route prefetching
  prefetchRoute: (path: string) => void;
}

const initialFormData: Partial<RegistrationFormValues> = {
  // Signup fields
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  zipCode: '',
  address: '',
  city: '',
  state: '',
  middleNameInfo: {
    noMiddlename: true,
    middleName: '',
  },
  dateOfBirth: '',

  // Cleaner info fields
  profilePic: '',
  bio: '',
  languages: [],
  workPreferences: [],
  positionAddress: { lat: 0, lng: 0 },
  distanceToTravel: 3,

  // Security check fields
  ssn: '',
  sendBackgroundCheckCopy: false,
  backgroundCheckAccepted: false,
};

// Add utility function to sanitize form data before persisting
const sanitizeFormData = (formData: Partial<RegistrationFormValues>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ssn, ...safeData } = formData;
  return safeData;
};

// Helper function to get fields for each step
const getStepFields = (step: number): (keyof RegistrationFormValues)[] => {
  switch (step) {
    case STEPS.SIGNUP_CLEANER:
      return [
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'zipCode',
        'address',
        'city',
        'state',
        'middleNameInfo',
        'dateOfBirth',
      ];
    case STEPS.PERSONAL_INFO:
      return [
        'profilePic',
        'bio',
        'languages',
        'workPreferences',
        'positionAddress',
        'distanceToTravel',
      ];
    case STEPS.BACKGROUND_CHECK:
      return ['ssn', 'sendBackgroundCheckCopy', 'backgroundCheckAccepted'];
    default:
      return [];
  }
};

export const useCleanerRegistrationStore = create<CleanerRegistrationState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Current step
        currentStep: STEPS.SIGNUP_CLEANER,
        setCurrentStep: step => {
          const state = get();
          if (state.canProceedToStep(step)) {
            set({ currentStep: step });
          }
        },
        nextStep: () => {
          const state = get();
          const nextStep = state.getNextAccessibleStep();
          if (nextStep > state.currentStep) {
            set({ currentStep: nextStep });
          }
        },
        previousStep: () => {
          const state = get();
          const prevStep = state.getPreviousAccessibleStep();
          if (prevStep < state.currentStep) {
            set({ currentStep: prevStep });
          }
        },
        jumpToStep: step => {
          const state = get();
          if (state.canProceedToStep(step)) {
            set({ currentStep: step });
          }
        },

        // Form data
        formData: initialFormData,
        updateFormData: data => {
          const state = get();
          set({
            formData: { ...state.formData, ...data },
            isDirty: true,
            lastSaved: null,
          });
        },
        setFormData: data =>
          set({
            formData: data,
            isDirty: true,
            lastSaved: null,
          }),
        updateFormField: (field, value) => {
          const state = get();
          set({
            formData: { ...state.formData, [field]: value },
            isDirty: true,
            lastSaved: null,
          });
        },
        resetFormData: () =>
          set({
            formData: initialFormData,
            isDirty: false,
            lastSaved: null,
          }),

        // Validation
        errors: {},
        setErrors: errors => set({ errors }),
        clearErrors: () => set({ errors: {} }),
        validateCurrentStep: () => {
          const state = get();
          const stepFields = getStepFields(state.currentStep);
          const stepData: Record<string, unknown> = {};

          // Extract only the fields for current step
          stepFields.forEach(field => {
            if (field === 'middleNameInfo' && state.formData[field]) {
              stepData[field] = {
                noMiddlename: state.formData[field]?.noMiddlename ?? true,
                middleName: state.formData[field]?.middleName ?? '',
              };
            } else {
              stepData[field] = state.formData[field];
            }
          });

          // Create step-specific schema
          const stepSchema = registrationSchema.pick(
            Object.fromEntries(stepFields.map(f => [f, true])) as Record<
              string,
              true
            >
          );

          const result = stepSchema.safeParse(stepData);

          if (!result.success) {
            const errors: Partial<
              Record<keyof RegistrationFormValues, string[]>
            > = {};
            result.error.issues.forEach(issue => {
              const path = issue.path[0] as keyof RegistrationFormValues;
              if (!errors[path]) errors[path] = [];
              errors[path]?.push(issue.message);
            });

            set({ errors });
            return { isValid: false, errors };
          }

          // Clear errors if valid
          set({ errors: {} });
          return { isValid: true, errors: {} };
        },
        isStepValid: step => {
          const state = get();
          const stepFields = getStepFields(step);

          // Check if all required fields for this step are filled
          return stepFields.every(field => {
            const value = state.formData[field];
            if (field === 'middleNameInfo') {
              return (
                value && typeof value === 'object' && 'noMiddlename' in value
              );
            }
            if (Array.isArray(value)) {
              return value.length > 0;
            }
            if (typeof value === 'boolean') {
              return value === true; // For acceptance checkboxes
            }
            return value !== undefined && value !== null && value !== '';
          });
        },

        // Step completion status
        completedSteps: new Set(),
        markStepCompleted: step => {
          const state = get();
          const newCompletedSteps = new Set(state.completedSteps);
          newCompletedSteps.add(step);

          // If marking current step as complete, try to mark previous steps too
          if (step === state.currentStep) {
            const validation = state.validateCurrentStep();
            if (validation.isValid) {
              // Also mark previous steps if accessible
              for (let i = STEPS.SIGNUP_CLEANER; i < step; i++) {
                if (state.canProceedToStep(i)) {
                  newCompletedSteps.add(i);
                }
              }
            }
          }

          set({ completedSteps: newCompletedSteps });
        },
        unmarkStepCompleted: step => {
          const state = get();
          const newCompletedSteps = new Set(state.completedSteps);
          newCompletedSteps.delete(step);
          set({ completedSteps: newCompletedSteps });
        },
        isStepCompleted: step => get().completedSteps.has(step),
        getStepProgress: () => {
          const state = get();
          const isPersonalInfoDisabled =
            isPersonalInfoStepDisabledWithOverride();
          const totalSteps = isPersonalInfoDisabled ? 2 : 3; // SIGNUP, PERSONAL_INFO (optional), BACKGROUND_CHECK
          const completed = state.completedSteps.size;
          const percentage = Math.round((completed / totalSteps) * 100);

          return {
            completed,
            total: totalSteps,
            percentage,
          };
        },

        // Registration status
        isSignedUp: false,
        setIsSignedUp: signedUp => {
          const state = get();
          set({ isSignedUp: signedUp });

          // Auto-mark signup step as completed
          if (signedUp) {
            state.markStepCompleted(STEPS.SIGNUP_CLEANER);

            // If personal info step is disabled, also mark it as completed
            if (isPersonalInfoStepDisabledWithOverride()) {
              state.markStepCompleted(STEPS.PERSONAL_INFO);
            }
          }
        },

        // Loading states
        isLoading: false,
        setIsLoading: loading => set({ isLoading: loading }),
        loadingStep: null,
        setLoadingStep: step => set({ loadingStep: step }),

        // Navigation helpers
        canProceedToStep: step => {
          const state = get();
          const isPersonalInfoDisabled =
            isPersonalInfoStepDisabledWithOverride();

          if (step === STEPS.SIGNUP_CLEANER) return true;
          if (step === STEPS.PERSONAL_INFO)
            return isPersonalInfoDisabled ? false : state.isSignedUp;
          if (step === STEPS.BACKGROUND_CHECK) {
            if (isPersonalInfoDisabled) {
              return state.isSignedUp; // Can proceed directly from signup
            }
            return state.isStepCompleted(STEPS.PERSONAL_INFO);
          }
          if (step === STEPS.COMPLETED)
            return state.isStepCompleted(STEPS.BACKGROUND_CHECK);
          return false;
        },
        getFirstIncompleteStep: () => {
          const state = get();
          const isPersonalInfoDisabled =
            isPersonalInfoStepDisabledWithOverride();

          if (!state.isSignedUp) return STEPS.SIGNUP_CLEANER;
          if (
            !isPersonalInfoDisabled &&
            !state.isStepCompleted(STEPS.PERSONAL_INFO)
          )
            return STEPS.PERSONAL_INFO;
          if (!state.isStepCompleted(STEPS.BACKGROUND_CHECK))
            return STEPS.BACKGROUND_CHECK;
          return STEPS.COMPLETED;
        },
        getNextAccessibleStep: () => {
          const state = get();
          for (
            let step = state.currentStep + 1;
            step <= STEPS.COMPLETED;
            step++
          ) {
            if (state.canProceedToStep(step)) {
              return step;
            }
          }
          return state.currentStep;
        },
        getPreviousAccessibleStep: () => {
          const state = get();
          for (
            let step = state.currentStep - 1;
            step >= STEPS.SIGNUP_CLEANER;
            step--
          ) {
            if (state.canProceedToStep(step)) {
              return step;
            }
          }
          return state.currentStep;
        },
        getAllAccessibleSteps: () => {
          const state = get();
          const accessible: number[] = [];
          for (
            let step = STEPS.SIGNUP_CLEANER;
            step <= STEPS.COMPLETED;
            step++
          ) {
            if (state.canProceedToStep(step)) {
              accessible.push(step);
            }
          }
          return accessible;
        },

        // Auto-save
        lastSaved: null,
        setLastSaved: timestamp => set({ lastSaved: timestamp }),
        isDirty: false,
        setIsDirty: dirty => set({ isDirty: dirty }),

        // Error handling
        error: null,
        setError: error => set({ error }),
        clearError: () => set({ error: null }),

        // Reset utility
        reset: () =>
          set({
            currentStep: STEPS.SIGNUP_CLEANER,
            formData: initialFormData,
            completedSteps: new Set(),
            isSignedUp: false,
            errors: {},
            isLoading: false,
            loadingStep: null,
            lastSaved: null,
            isDirty: false,
            error: null,
          }),
        clearAllData: () => {
          // Complete reset including localStorage
          localStorage.removeItem('cleaner-registration-storage');
          get().reset();
        },

        // Route prefetching
        prefetchRoute: (path: string) => {
          // This will be implemented at the router level
          // For now, we'll use a simple import-based prefetching
          const routeMap: Record<string, () => Promise<unknown>> = {
            '/apply-cleaner/signup': () =>
              import('@/features/cleaner/apply-cleaner/steps/cleaner-signup-step'),
            '/apply-cleaner/personal-info': () =>
              import('@/features/cleaner/apply-cleaner/steps/personal-info-step'),
            '/apply-cleaner/background-check': () =>
              import('@/features/cleaner/apply-cleaner/steps/background-check-step'),
            '/apply-cleaner/completed': () =>
              import('@/features/cleaner/apply-cleaner/steps/completed-step'),
          };

          const prefetcher = routeMap[path];
          if (prefetcher) {
            prefetcher().catch(() => {
              // Ignore prefetch errors
            });
          }
        },
      }),
      {
        name: 'cleaner-registration-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
          // Only persist these fields (sanitized)
          currentStep: state.currentStep,
          formData: sanitizeFormData(state.formData), // Sanitize before persisting
          completedSteps: Array.from(state.completedSteps),
          isSignedUp: state.isSignedUp,
          lastSaved: state.lastSaved,
        }),
        onRehydrateStorage: () => state => {
          // Convert array back to Set after rehydration
          if (state?.completedSteps) {
            state.completedSteps = new Set(
              state.completedSteps as unknown as number[]
            );
          }
          // Mark as not dirty on rehydration
          if (state) {
            state.isDirty = false;

            // If personal info step is disabled and user is signed up, mark it as completed
            if (isPersonalInfoStepDisabledWithOverride() && state.isSignedUp) {
              state.completedSteps.add(STEPS.PERSONAL_INFO);
            }
          }
        },
        version: 2, // Bump version for migration support (removed signupToken, added SSN sanitization)
      }
    )
  )
);

// Auto-save subscription
let autoSaveTimeout: NodeJS.Timeout;

useCleanerRegistrationStore.subscribe(
  state => state.formData,
  (_formData, _prevState) => {
    const store = useCleanerRegistrationStore.getState();

    // Only auto-save if data is dirty and not loading
    if (store.isDirty && !store.isLoading) {
      // Clear previous timeout
      clearTimeout(autoSaveTimeout);

      // Set new timeout for 500ms debounce
      autoSaveTimeout = setTimeout(() => {
        useCleanerRegistrationStore
          .getState()
          .setLastSaved(new Date().toISOString());
      }, 500);
    }
  },
  {
    equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  }
);
